/*
 * Soft weather layer — currently just a drizzle. No thunder, no game-impacting
 * effects. Per CLAUDE.md the world is cozy not cinematic; weather here is a
 * texture, not an event.
 *
 * Visual recipe (per AESTHETIC.md §6.6 "no god rays / volumetric fog"):
 *  - Small angled streaks at low alpha (cream-leaning, never blue-white).
 *  - The whole layer fades in/out via WorldState.weatherT so it never snaps on.
 *  - We re-use the firefly-style "simulate-always, render-conditionally" idea:
 *    a fixed pool of raindrops is always updating, and we just dim them by
 *    weatherT — there's no spawn/despawn churn.
 *
 * The wind effect on fireflies is exported as `weatherWindX(state)` so game.ts
 * can pass it into the firefly update each frame.
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD } from '@/render/palette';
import type { WorldState } from '@/state/world';

const DROP_COUNT = 64;

/** Streak angle (radians) — gentle 15deg lean off vertical. */
const ANGLE = (15 * Math.PI) / 180;

/** Drop fall speed in world-units / second. */
const DROP_SPEED = 380;

/** Each drop is a short streak this long (world units). */
const DROP_LENGTH = 9;

interface RainDrop {
  x: number;
  y: number;
  /** Per-drop alpha jitter so the curtain isn't uniform. */
  jitter: number;
}

export interface WeatherView {
  container: Container;
  update(dtSec: number, state: WorldState): void;
}

/**
 * Read the horizontal wind component for a given world state. Returns world
 * units to add per frame to firefly x. Zero outside weather windows.
 *
 * Exposed as a free function so game.ts can compute the firefly wind without
 * the weather view as a dependency.
 */
export function weatherWindX(state: WorldState): number {
  if (state.weather !== 'drizzle') return 0;
  // ~0.35 px/frame at full drizzle, in the same direction as the rain lean.
  return state.weatherT * 0.35;
}

export function createWeather(): WeatherView {
  const root = new Container();
  const g = new Graphics();
  root.addChild(g);

  // Spread drops across the full world height so the curtain looks active
  // from the first frame the weather appears.
  const drops: RainDrop[] = Array.from({ length: DROP_COUNT }, () => ({
    x: Math.random() * WORLD.width,
    y: Math.random() * WORLD.height,
    jitter: 0.6 + Math.random() * 0.4,
  }));

  // dx/dy per frame are computed from ANGLE + DROP_SPEED. We move along the
  // streak direction, not straight down, so the visible streak and the motion
  // path align.
  const dirX = Math.sin(ANGLE);
  const dirY = Math.cos(ANGLE);

  function update(dtSec: number, state: WorldState): void {
    g.clear();
    const moveStep = DROP_SPEED * dtSec;

    for (const d of drops) {
      d.x += dirX * moveStep;
      d.y += dirY * moveStep;
      // Wrap. Allow drops to enter from the top-left bias so wrapping doesn't
      // look like a teleport at the right edge.
      if (d.y > WORLD.height + DROP_LENGTH) {
        d.y = -DROP_LENGTH;
        d.x = Math.random() * (WORLD.width + 60) - 30;
      }
      if (d.x > WORLD.width + DROP_LENGTH) {
        d.x -= WORLD.width + DROP_LENGTH * 2;
      }
      if (d.x < -DROP_LENGTH) {
        d.x += WORLD.width + DROP_LENGTH * 2;
      }
    }

    // Only paint if there's any weather visibility this frame.
    if (state.weatherT <= 0) return;

    // Streak alpha. Capped low so the rain reads as drizzle, not downpour.
    // We use a warm near-cream rather than pure white to stay within the
    // palette's no-cool-grays rule (AESTHETIC.md §1.5).
    const baseAlpha = 0.22 * state.weatherT;
    const tailX = -dirX * DROP_LENGTH;
    const tailY = -dirY * DROP_LENGTH;

    for (const d of drops) {
      const a = baseAlpha * d.jitter;
      g.moveTo(d.x, d.y)
        .lineTo(d.x + tailX, d.y + tailY)
        .stroke({ color: 0xd8d4c4, width: 0.9, alpha: a });
    }
  }

  return { container: root, update };
}
